import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Card,
  CardContent,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchStatuses,
  searchStatuses,
  fetchStatistics,
  bulkActivate,
  bulkDeactivate,
  bulkDelete,
  exportToCSV,
  setSearchParams,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  clearSuccessMessage,
} from '../../store/reducers/statusSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  searchSection: {
    marginBottom: theme.spacing(3),
  },
  statisticsCards: {
    marginBottom: theme.spacing(3),
  },
  statisticsCard: {
    textAlign: 'center',
  },
  statisticsValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    fontWeight: 'bold',
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  bulkActions: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  searchField: {
    marginRight: theme.spacing(2),
  },
  filterField: {
    marginRight: theme.spacing(2),
    minWidth: 120,
  },
}));

const StatusList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    items,
    loading,
    error,
    searchParams,
    pagination,
    statistics,
    selectedIds,
    bulkOperationLoading,
    successMessage,
  } = useSelector((state: RootState) => state.status);

  const [searchTerm, setSearchTerm] = useState(searchParams.search || '');
  const [activeFilter, setActiveFilter] = useState<string>(searchParams.active?.toString() || '');

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchStatuses());
    dispatch(fetchStatistics());
  }, [dispatch]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  const handleSearch = () => {
    const newSearchParams = {
      ...searchParams,
      search: searchTerm,
      active: activeFilter === '' ? undefined : activeFilter === 'true',
      page: 1, // Reset to first page when searching
    };
    dispatch(setSearchParams(newSearchParams));
    dispatch(searchStatuses(newSearchParams));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveFilter('');
    const newSearchParams = {
      ...searchParams,
      search: '',
      active: undefined,
      page: 1,
    };
    dispatch(setSearchParams(newSearchParams));
    dispatch(fetchStatuses(newSearchParams));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    const newSearchParams = {
      ...searchParams,
      page: newPage + 1,
    };
    dispatch(setSearchParams(newSearchParams));
    dispatch(searchStatuses(newSearchParams));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    const newSearchParams = {
      ...searchParams,
      pageSize: newPageSize,
      page: 1,
    };
    dispatch(setSearchParams(newSearchParams));
    dispatch(searchStatuses(newSearchParams));
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = items.map(item => item.Id!).filter(Boolean);
      dispatch(setSelectedIds(allIds));
    } else {
      dispatch(clearSelectedIds());
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      dispatch(addSelectedId(id));
    } else {
      dispatch(removeSelectedId(id));
    }
  };

  const handleBulkActivate = () => {
    if (selectedIds.length > 0) {
      dispatch(bulkActivate(selectedIds));
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedIds.length > 0) {
      dispatch(bulkDeactivate(selectedIds));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length > 0 && window.confirm(`Are you sure you want to delete ${selectedIds.length} Statuses?`)) {
      dispatch(bulkDelete(selectedIds));
    }
  };

  const handleExport = () => {
    dispatch(exportToCSV(searchParams));
  };

  const handleEdit = (id: number) => {
    navigate(`/catalogs/status/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/catalogs/status/view/${id}`);
  };

  const handleAdd = () => {
    navigate('/catalogs/status/add');
  };

  if (loading && items.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" gutterBottom>
          Statuses
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Manage System Statuses
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statisticsCards}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statisticsCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Statuses
              </Typography>
              <Typography className={classes.statisticsValue}>
                {statistics.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statisticsCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Statuses
              </Typography>
              <Typography className={classes.statisticsValue} style={{ color: 'green' }}>
                {statistics.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statisticsCard}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inactive Statuses
              </Typography>
              <Typography className={classes.statisticsValue} style={{ color: 'red' }}>
                {statistics.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper className={classes.searchSection}>
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Statuses"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className={classes.searchField}
                InputProps={{
                  endAdornment: <SearchIcon color="action" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth className={classes.filterField}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value as string)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                style={{ marginRight: 8 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                style={{ marginRight: 8 }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExport}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <Box className={classes.bulkActions}>
          <Typography variant="h6" gutterBottom>
            Bulk Actions ({selectedIds.length} selected)
          </Typography>
          <Box className={classes.actionButtons}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBulkActivate}
              disabled={bulkOperationLoading}
              startIcon={<ActiveIcon />}
            >
              Activate
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBulkDeactivate}
              disabled={bulkOperationLoading}
              startIcon={<InactiveIcon />}
            >
              Deactivate
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBulkDelete}
              disabled={bulkOperationLoading}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}

      {/* Actions Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Statuses ({pagination.total})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add New Status
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedIds.length === items.length && items.length > 0}
                  indeterminate={selectedIds.length > 0 && selectedIds.length < items.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Status Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => (
                <TableRow key={item.Id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(item.Id!)}
                      onChange={(e) => handleSelectItem(item.Id!, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {item.StatusName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {item.Description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.Active ? 'Active' : 'Inactive'}
                      className={`${classes.statusChip} ${
                        item.Active ? classes.activeChip : classes.inactiveChip
                      }`}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.actionButtons}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleView(item.Id!)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item.Id!)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this Status?')) {
                              // Handle delete
                            }
                          }}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No Statuses found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={pagination.total}
        rowsPerPage={pagination.pageSize}
        page={pagination.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default StatusList; 